/**
 * A свиня or a тур: boxes on a procedural skin, the same way the cossacks are
 * built, but on four legs.
 *
 * The whole body plan lives here and the pixels live in core - so the shape a
 * player sees and the texture painted onto it come from one description, and
 * "which face is the animal's front" is answered in one place.
 *
 * Yaw 0 faces -Z, the same as everything else in this game that has a front.
 */

import * as THREE from "three";
import {
  faceUV, SKIN_TEX, type BoxFaces, type BoxSize, type Rect,
} from "../core/player/skins.js";
import {
  layoutBeast, paintBeast, BEAST_SKINS,
} from "../core/entity/beastSkin.js";
import { BREEDS, type BeastKind } from "../core/entity/beast.js";
import { dragonHeadLocal } from "../core/entity/dragon.js";

/**
 * Everything a species shares, built once and never disposed.
 *
 * Every pig is the same pig: the same texture, the same material, and the
 * same ten boxes at the same sizes. Building them per animal meant a herd of
 * fourteen held a hundred and forty buffer geometries for ten distinct
 * shapes, and it grew with every animal added. A geometry costs a GPU buffer
 * whether or not it is a copy of one already there.
 */
interface SpeciesGpu {
  texture: THREE.Texture;
  material: THREE.MeshBasicMaterial;
  parts: Map<string, THREE.BufferGeometry>;
}
const species = new Map<BeastKind, SpeciesGpu>();

function speciesGpu(kind: BeastKind): SpeciesGpu {
  const found = species.get(kind);
  if (found !== undefined) return found;

  const def = BEAST_SKINS[kind];
  const data = new Uint8Array(paintBeast(def));
  const texture = new THREE.DataTexture(data, SKIN_TEX, SKIN_TEX);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.flipY = true;
  texture.needsUpdate = true;

  // Unlit, like the terrain, the cossacks and the orcs. There is not one
  // light source in this scene: the day/night cycle is a colour multiply in
  // the shader, and a material that wants a lamp renders pure black. Every
  // animal came out as a silhouette the first time, for exactly that.
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    // Ears and horns are thin boxes seen edge-on from some angles; without
    // this they wink out as you walk round the animal.
    side: THREE.DoubleSide,
  });

  const p = def.parts;
  const layout = layoutBeast(p);
  const px = BREEDS[kind].height / p.standing;
  const parts = new Map<string, THREE.BufferGeometry>();
  const sizes: [string, typeof p.body][] = [
    ["body", p.body], ["head", p.head], ["muzzle", p.muzzle],
    ["legFR", p.leg], ["legFL", p.leg], ["legBR", p.leg], ["legBL", p.leg],
    ["hornR", p.horn], ["hornL", p.horn], ["tail", p.tail],
  ];
  if (p.wing !== undefined) sizes.push(["wingR", p.wing], ["wingL", p.wing]);
  if (p.crest !== undefined) sizes.push(["crest", p.crest]);
  if (p.neck !== undefined) sizes.push(["neck", p.neck]);
  // The Змій's other two of everything. Without this the layout has them and
  // the GPU does not, so `part()` hands back null and he comes out with one
  // head - which is what the first render of him had.
  if (p.heads === 3) {
    sizes.push(["headR", p.head], ["headL", p.head]);
    sizes.push(["muzzleR", p.muzzle], ["muzzleL", p.muzzle]);
    if (p.neck !== undefined) sizes.push(["neckR", p.neck], ["neckL", p.neck]);
  }
  for (const [name, size] of sizes) {
    const faces = layout[name as keyof typeof layout];
    if (faces === undefined) continue;
    const geo = new THREE.BoxGeometry(size.w * px, size.h * px, size.d * px);
    applySkinUVs(geo, faces);
    parts.set(name, geo);
  }

  const built: SpeciesGpu = { texture, material, parts };
  species.set(kind, built);
  return built;
}

/**
 * Write skin UVs onto a BoxGeometry.
 *
 * Three.js orders box faces +X, -X, +Y, -Y, +Z, -Z. The model faces -Z, so -Z
 * is the animal's front and +X is its right. Same convention and same face
 * order as the cossack model - if these two ever disagree, one of the two
 * species comes out inside-out.
 */
function applySkinUVs(geometry: THREE.BufferGeometry, faces: BoxFaces): void {
  const order: Rect[] = [
    faces.right, faces.left,
    faces.top, faces.bottom,
    faces.back, faces.front,
  ];
  const uv = geometry.getAttribute("uv") as THREE.BufferAttribute;
  order.forEach((r, faceIndex) => {
    const { u0, u1, vTop, vBottom } = faceUV(r);
    const base = faceIndex * 4;
    uv.setXY(base + 0, u0, vTop);
    uv.setXY(base + 1, u1, vTop);
    uv.setXY(base + 2, u0, vBottom);
    uv.setXY(base + 3, u1, vBottom);
  });
  uv.needsUpdate = true;
}

/** What an animal goes towards when it is struck. See PlayerModel. */
const HURT_COLOUR = new THREE.Color(1, 0.28, 0.22);

export class BeastModel {
  readonly group = new THREE.Group();
  private readonly material: THREE.MeshBasicMaterial;
  /** The four legs, in the order front-right, front-left, back-right, back-left. */
  private readonly legs: THREE.Group[] = [];
  private readonly head: THREE.Group;
  private readonly wings: THREE.Mesh[] = [];
  /** What each wing's roll is with the animal standing still. */
  private readonly wingRest: number[] = [];
  /** Where the head rests, so the walking bob has something to return to. */
  private headZ = 0;
  private readonly tint = new THREE.Color(1, 1, 1);
  private hurt = 0;

  constructor(kind: BeastKind) {
    const def = BEAST_SKINS[kind];
    const p = def.parts;
    // Blocks per skin pixel, so the animal stands exactly as tall as the
    // thing that collides with the world thinks it does.
    const px = BREEDS[kind].height / p.standing;
    const gpu = speciesGpu(kind);
    /*
     * A copy of the species' material, not the species' material.
     *
     * The texture inside is still shared, which is the expensive part. The
     * material is a handful of numbers, and sharing it meant a colour set on
     * one animal was set on the whole herd - so hitting one pig would have
     * lit up every pig on the island. Nobody noticed while the only colour
     * anybody set was the daylight, which they all get equally.
     */
    this.material = gpu.material.clone();

    // Every mesh points at the species' one geometry for that part. A model
    // owns its Groups and its place in the world and nothing else.
    const part = (_size: BoxSize, name: string): THREE.Mesh | null => {
      const geo = gpu.parts.get(name);
      if (geo === undefined) return null;
      return new THREE.Mesh(geo, this.material);
    };

    // The body: a long box lying along Z, its underside at leg height.
    const bodyY = p.leg.h * px + (p.body.h * px) / 2;
    const body = part(p.body, "body");
    if (body !== null) {
      body.position.set(0, bodyY, 0);
      this.group.add(body);
    }

    /*
     * Where the head goes, which is the whole difference between the two
     * body plans.
     *
     * A pig and a тур carry theirs off the front of the body and below the
     * spine; a hen carries hers on top of a neck she holds up, forward of
     * the breast and clear above it. Drawn the beast's way, a курка came out
     * as a brown loaf with a beak sticking out of one end.
     */
    const dragon = def.stance === "dragon";
    this.head = new THREE.Group();
    this.head.position.set(
      0,
      dragon
        // Held high on a neck that rises off the shoulders, which is what
        // the drawing has and what makes him loom rather than lumber. The
        // numbers come from core, where the breath leaves from the same
        // place - see `dragonHeadLocal`.
        ? dragonHeadLocal().up
        : def.stance === "bird"
        ? bodyY + (p.body.h * px) / 2 + (p.head.h * px) / 2
        : bodyY + (p.body.h * px) / 2 - (p.head.h * px) / 2,
      dragon
        ? -dragonHeadLocal().forward
        : def.stance === "bird"
        ? -(p.body.d * px) / 2 - (p.head.d * px) / 2 + 2 * px
        : -(p.body.d * px) / 2 - (p.head.d * px) / 2 + px,
    );
    this.headZ = this.head.position.z;
    const head = part(p.head, "head");
    if (head !== null) this.head.add(head);

    const muzzle = part(p.muzzle, "muzzle");
    if (muzzle !== null) {
      muzzle.position.set(
        0,
        // A beak sits just under the eyes; a snout hangs lower still.
        def.stance === "bird" ? -(p.head.h * px) / 6 : -(p.head.h * px) / 4,
        -(p.head.d * px) / 2 - (p.muzzle.d * px) / 2,
      );
      this.head.add(muzzle);
    }

    // Ears lie back along the head; horns stand out sideways from the top of
    // it. One box each side either way, mirrored.
    for (const [name, sign] of [["hornR", 1], ["hornL", -1]] as const) {
      const horn = part(p.horn, name);
      if (horn === null) continue;
      if (def.headgear === "comb") {
        /*
         * One гребінь, not two.
         *
         * A comb stands along the middle of the head, so the left-hand box
         * is simply not hung - it is painted all the same, because dropping
         * it from the layout would move every part after it on the texture
         * and repaint two animals that had nothing to do with this.
         *
         * And it leans: the чубата's comb falls to one side, pushed over by
         * the tuft behind it, which is in every photograph of the breed.
         */
        if (sign < 0) continue;
        horn.position.set(0, (p.head.h * px) / 2 + (p.horn.h * px) / 2 - px, -px);
        horn.rotation.z = 0.32;
        this.head.add(horn);
        continue;
      }
      if (def.headgear === "horns") {
        horn.position.set(
          sign * ((p.head.w * px) / 2 + (p.horn.w * px) / 2 - px),
          (p.head.h * px) / 2 - px,
          -px,
        );
        // Swept forward, which is what the drawing of a тур has and what
        // makes horns read as horns rather than as a yoke.
        horn.rotation.z = sign * -0.25;
        horn.rotation.y = sign * 0.35;
      } else {
        horn.position.set(
          sign * ((p.head.w * px) / 2 - px),
          (p.head.h * px) / 2 + px,
          px,
        );
        horn.rotation.x = 0.5;
        horn.rotation.z = sign * 0.3;
      }
      this.head.add(horn);
    }
    this.group.add(this.head);

    /*
     * The neck, between the shoulders and the head.
     *
     * Hung on the body rather than on the head, and leaning forward to meet
     * it: without one the head sat straight on the back and the whole bird
     * read as a brown column with a beak on the front of it.
     */
    if (p.neck !== undefined) {
      const neckMesh = part(p.neck, "neck");
      if (neckMesh !== null) {
        neckMesh.position.set(
          0,
          bodyY + (p.body.h * px) / 2,
          -(p.body.d * px) / 2 + (p.neck.d * px) / 2,
        );
        neckMesh.rotation.x = 0.5;
        this.group.add(neckMesh);
      }
    }

    // The чуб, behind the comb and on top of the head.
    const crest = part(p.crest ?? p.horn, "crest");
    if (crest !== null && p.crest !== undefined) {
      crest.position.set(0, (p.head.h * px) / 2 + (p.crest.h * px) / 2 - px, px);
      crest.rotation.x = -0.2;
      this.head.add(crest);
    }

    /*
     * Wings, flat against the flanks.
     *
     * A hen's wings are folded almost all the time - she runs rather than
     * flies - so these are plates on her sides that lift when she bolts,
     * which is the only occasion she uses them and the reason they are here.
     */
    if (p.wing !== undefined) {
      for (const [name, sign] of [["wingR", 1], ["wingL", -1]] as const) {
        const wing = part(p.wing, name);
        if (wing === null) continue;
        wing.position.set(
          sign * ((p.body.w * px) / 2 + (p.wing.w * px) / 2 - px / 2),
          // High on the flank: a folded wing sits along the top of the side,
          // not across the middle of it.
          dragon
            ? bodyY + (p.body.h * px) / 2 - px
            : bodyY + (p.body.h * px) / 4,
          dragon ? (p.body.d * px) * 0.1 : px,
        );
        // A hen's are folded against her; a Змій's are out, half-raised, the
        // way the drawing has them - and they are what he reads as from any
        // distance at all.
        if (dragon) {
          // Tips up, not down. A point out along +X rotated about +Z by a
          // positive angle rises, and by a negative one falls - so the two
          // sides take opposite signs, and the first version had them the
          // wrong way round: a dragon whose wings droop like wet washing.
          wing.rotation.z = sign * 0.85;
          // And swept back a little, which is what stops a spread wing
          // reading as an outstretched arm.
          wing.rotation.y = sign * 0.10;
        }
        this.wings.push(wing);
        this.wingRest.push(wing.rotation.z);
        this.group.add(wing);
      }
    }

    /*
     * Legs. Four on a beast, one pair on a bird.
     *
     * A hen's two go under the middle of her, not at the corners: her weight
     * is over her feet, which is why she can stand still on them. A bird
     * with legs at the front corners looks as though it is about to fall
     * forward, because it is.
     */
    const twoLegged = def.stance === "bird";
    const legX = twoLegged
      ? (p.leg.w * px)
      : (p.body.w * px) / 2 - (p.leg.w * px) / 2;
    const legZ = (p.body.d * px) / 2 - (p.leg.d * px) / 2 - px;
    const corners: [number, number][] = twoLegged
      ? [[legX, 0], [-legX, 0]]
      : [[legX, -legZ], [-legX, -legZ], [legX, legZ], [-legX, legZ]];
    const legNames = ["legFR", "legFL", "legBR", "legBL"] as const;
    corners.forEach(([x, z], i) => {
      const pivot = new THREE.Group();
      pivot.position.set(x, p.leg.h * px, z);
      const mesh = part(p.leg, legNames[i]!);
      if (mesh !== null) {
        mesh.position.y = -(p.leg.h * px) / 2;
        pivot.add(mesh);
      }
      this.legs.push(pivot);
      this.group.add(pivot);
    });

    /*
     * And the other two heads, and the three necks under them.
     *
     * Built here rather than in the block above because a head is a Group
     * with a muzzle and horns in it, and the middle one is already that: the
     * outer pair are the same thing again, swung out to each side and turned
     * to look outwards. The drawing has them low and wide, which is what
     * reads as three heads from in front - three in a row at the same height
     * reads as one head with lumps.
     */
    if (dragon && p.neck !== undefined) {
      const shoulderY = bodyY + (p.body.h * px) / 2;
      const shoulderZ = -(p.body.d * px) / 2;
      const heads: { group: THREE.Group; out: number }[] = [
        { group: this.head, out: 0 },
      ];
      for (const [side, sign] of [["R", 1], ["L", -1]] as const) {
        const other = new THREE.Group();
        other.position.set(
          sign * (p.head.w * px) * 0.95,
          shoulderY + (p.neck.d * px) * 0.40,
          shoulderZ - (p.neck.d * px) * 0.42,
        );
        other.rotation.y = sign * -0.55;
        const skull = part(p.head, `head${side}`);
        if (skull !== null) other.add(skull);
        const jaw = part(p.muzzle, `muzzle${side}`);
        if (jaw !== null) {
          jaw.position.set(
            0, -(p.head.h * px) / 4,
            -(p.head.d * px) / 2 - (p.muzzle.d * px) / 2,
          );
          other.add(jaw);
        }
        this.group.add(other);
        heads.push({ group: other, out: sign });
      }
      /*
       * A neck to each of them, leaning from the shoulders to the head.
       *
       * Aimed rather than placed: the box is put halfway along the line from
       * the shoulder to the head it carries and turned to lie along it, so a
       * head moved for any reason drags its own neck with it instead of
       * leaving a gap.
       */
      for (const { group, out } of heads) {
        const name = out === 0 ? "neck" : out > 0 ? "neckR" : "neckL";
        const mesh = part(p.neck, name);
        if (mesh === null) continue;
        const to = group.position;
        mesh.position.set(
          to.x / 2, (shoulderY + to.y) / 2, (shoulderZ + to.z) / 2,
        );
        /*
         * Aimed with `lookAt` and stretched to reach.
         *
         * The box is built along its own +Z, which is what `lookAt` points
         * at the target, so pointing it at the head it carries is the whole
         * of the geometry. Stretching it to the distance is what closes the
         * gap: hand-written angles left every head floating a little way off
         * its own neck, and three heads floating is not a dragon, it is
         * three heads floating.
         */
        mesh.lookAt(to.x, to.y, to.z);
        const reach = Math.hypot(to.x, to.y - shoulderY, to.z - shoulderZ);
        mesh.scale.z = reach / (p.neck.d * px);
        this.group.add(mesh);
      }
    }

    const tail = part(p.tail, "tail");
    if (tail !== null) {
      // Up and back off the rump, the way a hen carries hers - and touching
      // it: a tail floating behind the animal with daylight between reads as
      // something it is being chased by. A dog and a cat carry theirs the
      // same way; a pig and a тур let theirs hang.
      const up = def.stance === "bird" || p.tailUp === true;
      if (dragon) {
        // Off the back of the body and drooping: a tail held straight out
        // behind is a plank, and his is the longest thing about him.
        tail.position.set(
          0,
          bodyY - (p.tail.h * px) / 4,
          (p.body.d * px) / 2 + (p.tail.d * px) / 2 - px,
        );
        tail.rotation.x = 0.22;
        this.group.add(tail);
        return;
      }
      tail.position.set(
        0,
        up
          ? bodyY + (p.body.h * px) / 2 + (p.tail.h * px) / 4
          : bodyY + (p.body.h * px) / 2 - (p.tail.h * px) / 2,
        up
          ? (p.body.d * px) / 2 - px
          : (p.body.d * px) / 2 + px / 2,
      );
      if (up) tail.rotation.x = -0.45;
      this.group.add(tail);
    }
  }

  /**
   * Point it where it is going and swing its legs.
   *
   * Diagonal pairs move together - front-right with back-left - which is how
   * a four-legged animal actually walks, and the difference is visible even
   * on boxes.
   */
  /** Where the head's middle is in the world, for the stage to check. */
  headWorld(): { x: number; y: number; z: number } {
    this.group.updateMatrixWorld(true);
    const v = new THREE.Vector3();
    this.head.getWorldPosition(v);
    return { x: v.x, y: v.y, z: v.z };
  }

  setPose(yaw: number, phase: number, stride: number): void {
    this.group.rotation.y = yaw;
    const swing = Math.sin(phase) * 0.6 * stride;
    this.legs[0]!.rotation.x = swing;
    this.legs[1]!.rotation.x = -swing;
    // Diagonal pairs on a four-legged animal - front-right with back-left -
    // which is how one actually walks, and visible even on boxes.
    if (this.legs.length === 4) {
      this.legs[3]!.rotation.x = swing;
      this.legs[2]!.rotation.x = -swing;
    }
    /*
     * The head bobs with the stride, which is most of what makes it look
     * like an animal rather than a table walking - and a hen's bobs with
     * every step, forward and back, which is the one thing everybody knows
     * about how a chicken walks.
     */
    const bird = this.wings.length > 0;
    this.head.rotation.x = Math.sin(phase * 2) * (bird ? 0.2 : 0.06) * stride;
    if (bird) this.head.position.z = this.headZ + Math.sin(phase) * 0.035 * stride;
    /*
     * And the wings come away from the body while she is running.
     *
     * From wherever they rest, not from flat. A hen's rest folded against
     * her flanks and a Змій's rest half-raised, and this used to overwrite
     * the angle the constructor set - so his wings came out as two flat bars
     * sticking straight out sideways, whatever was written when they were
     * built. The resting angle is remembered and the beat is added to it.
     */
    this.wings.forEach((wing, i) => {
      const side = wing.position.x > 0 ? 1 : -1;
      wing.rotation.z = this.wingRest[i]! +
        side * (0.1 + Math.abs(Math.sin(phase)) * 0.5 * stride);
    });
  }

  setTint(color: THREE.Color): void {
    this.tint.copy(color);
    this.applyColour();
  }

  /**
   * Show that a blow just landed, 1 down to 0.
   *
   * The thing you could not see before: a swing connected, the health went
   * down, and the pig carried on grazing exactly as it had been.
   */
  setHurt(glow: number): void {
    if (glow === this.hurt) return;
    this.hurt = glow;
    this.applyColour();
  }

  private applyColour(): void {
    this.material.color.copy(this.tint).lerp(HURT_COLOUR, this.hurt);
  }

  /**
   * Give back this animal's own material.
   *
   * The geometry and the texture belong to the species and are left alone -
   * disposing those when one pig dies would blank every other pig on the map.
   * The material is this animal's copy and would otherwise leak, one per
   * animal that ever wandered into view.
   */
  dispose(): void {
    this.material.dispose();
  }
}
